import { useCallback, useEffect, useRef, useState } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { MapPin, Navigation, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { API_BASE } from "@/lib/utils";
import { haversineMeters, locationIsConfigured } from "@/lib/wallet-format";

const DEFAULT_CENTER = { lat: 1.2816, lng: 103.8636 };

function pinIcon() {
  return L.divIcon({
    className: "",
    html: `<div style="width:28px;height:28px;border-radius:50% 50% 50% 0;background:var(--primary);border:2px solid #fff;transform:rotate(-45deg);box-shadow:0 4px 12px rgba(0,0,0,.45)"></div>`,
    iconSize: [28, 28],
    iconAnchor: [14, 28],
  });
}

export default function VenueLocationPicker({ eventId, eventVenue, location, onChange }) {
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markerRef = useRef(null);
  const [geocoding, setGeocoding] = useState(false);
  const [proximity, setProximity] = useState(null);
  const [watching, setWatching] = useState(false);
  const watchIdRef = useRef(null);

  const setCoords = useCallback(
    (lat, lng, patch = {}) => {
      onChange({
        ...location,
        latitude: Number(lat.toFixed(6)),
        longitude: Number(lng.toFixed(6)),
        ...patch,
      });
    },
    [location, onChange]
  );

  useEffect(() => {
    if (!mapRef.current || mapInstanceRef.current) return undefined;
    const startLat = Number.isFinite(location.latitude) ? location.latitude : DEFAULT_CENTER.lat;
    const startLng = Number.isFinite(location.longitude) ? location.longitude : DEFAULT_CENTER.lng;
    const map = L.map(mapRef.current, { zoomControl: true }).setView([startLat, startLng], locationIsConfigured(location) ? 16 : 12);
    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      attribution: "&copy; OpenStreetMap",
      maxZoom: 19,
    }).addTo(map);

    const marker = L.marker([startLat, startLng], { draggable: true, icon: pinIcon() }).addTo(map);
    marker.on("dragend", () => {
      const pos = marker.getLatLng();
      setCoords(pos.lat, pos.lng);
    });
    map.on("click", (ev) => {
      marker.setLatLng(ev.latlng);
      setCoords(ev.latlng.lat, ev.latlng.lng);
    });

    mapInstanceRef.current = map;
    markerRef.current = marker;
    return () => {
      map.remove();
      mapInstanceRef.current = null;
      markerRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!markerRef.current || !mapInstanceRef.current) return;
    if (!Number.isFinite(location.latitude) || !Number.isFinite(location.longitude)) return;
    const latlng = L.latLng(location.latitude, location.longitude);
    markerRef.current.setLatLng(latlng);
    mapInstanceRef.current.setView(latlng, Math.max(mapInstanceRef.current.getZoom(), 15));
  }, [location.latitude, location.longitude]);

  const geocodeFromVenue = async () => {
    if (!eventId) return;
    setGeocoding(true);
    try {
      const res = await fetch(`${API_BASE}/api/events/${eventId}/wallet/geocode`, { method: "POST" });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || "Geocode failed");
      onChange({
        ...location,
        latitude: data.latitude,
        longitude: data.longitude,
        label: data.label || location.label,
        relevantText: location.relevantText || data.relevantText,
      });
      toast.success("Venue pin placed from event address");
    } catch (err) {
      toast.error("Could not locate venue", { description: err.message || "Check the venue field in Event basics." });
    } finally {
      setGeocoding(false);
    }
  };

  const testProximity = () => {
    if (!locationIsConfigured(location)) {
      toast.message("Drop a pin first", { description: "Set latitude/longitude or geocode from the event venue." });
      return;
    }
    if (!navigator.geolocation) {
      toast.error("Geolocation not available on this device");
      return;
    }
    if (watching && watchIdRef.current != null) {
      navigator.geolocation.clearWatch(watchIdRef.current);
      watchIdRef.current = null;
      setWatching(false);
      setProximity(null);
      return;
    }
    setWatching(true);
    watchIdRef.current = navigator.geolocation.watchPosition(
      (pos) => {
        const dist = haversineMeters(pos.coords.latitude, pos.coords.longitude, location.latitude, location.longitude);
        const inside = dist <= Number(location.radiusMeters || 150);
        setProximity({ dist, inside });
        if (inside) {
          toast.message(location.relevantText || "You're near the venue", {
            description: `${Math.round(dist)}m from pin · lock-screen reminder would show on wallet pass`,
          });
        }
      },
      (err) => {
        setWatching(false);
        toast.error("Location access denied", { description: err.message });
      },
      { enableHighAccuracy: true, maximumAge: 5000, timeout: 15000 }
    );
  };

  useEffect(
    () => () => {
      if (watchIdRef.current != null) navigator.geolocation.clearWatch(watchIdRef.current);
    },
    []
  );

  const configured = locationIsConfigured(location);

  return (
    <div className="space-y-3" data-testid="venue-location-picker">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="text-overline flex items-center gap-2">
          <MapPin size={11} className="text-[var(--primary)]" />
          Venue proximity pin
        </div>
        <span
          className={`text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 border ${
            configured ? "border-[var(--primary)]/50 text-[var(--primary)]" : "border-[var(--border-default)] text-[var(--text-tertiary)]"
          }`}
        >
          {configured ? "Geofence ready" : "Pin not set"}
        </span>
      </div>
      <p className="text-xs text-[var(--text-secondary)] leading-relaxed">
        Drop the pin on the venue entrance. Apple Wallet and Google Wallet show a lock-screen reminder when attendees are within{" "}
        {Number(location.radiusMeters || 150)}m.
      </p>

      <div ref={mapRef} className="h-[220px] w-full border border-[var(--border-default)] rounded-sm z-0" />

      <div className="grid grid-cols-2 gap-2">
        <label className="block">
          <span className="text-[10px] font-mono uppercase tracking-widest text-[var(--text-tertiary)]">Latitude</span>
          <input
            type="number"
            step="any"
            className="input-dark mt-1 font-mono text-xs w-full"
            value={location.latitude ?? ""}
            onChange={(ev) => {
              const v = ev.target.value === "" ? null : Number(ev.target.value);
              onChange({ ...location, latitude: Number.isFinite(v) ? v : null });
            }}
          />
        </label>
        <label className="block">
          <span className="text-[10px] font-mono uppercase tracking-widest text-[var(--text-tertiary)]">Longitude</span>
          <input
            type="number"
            step="any"
            className="input-dark mt-1 font-mono text-xs w-full"
            value={location.longitude ?? ""}
            onChange={(ev) => {
              const v = ev.target.value === "" ? null : Number(ev.target.value);
              onChange({ ...location, longitude: Number.isFinite(v) ? v : null });
            }}
          />
        </label>
      </div>

      <label className="block">
        <span className="text-[10px] font-mono uppercase tracking-widest text-[var(--text-tertiary)]">Lock-screen message</span>
        <input
          className="input-dark mt-1 text-xs w-full"
          value={location.relevantText || ""}
          onChange={(ev) => onChange({ ...location, relevantText: ev.target.value })}
          placeholder="You're near the venue — have your pass ready"
        />
      </label>

      <label className="block">
        <span className="text-[10px] font-mono uppercase tracking-widest text-[var(--text-tertiary)]">Radius (meters)</span>
        <input
          type="number"
          min={50}
          max={2000}
          className="input-dark mt-1 font-mono text-xs w-full max-w-[140px]"
          value={location.radiusMeters ?? 150}
          onChange={(ev) => onChange({ ...location, radiusMeters: Number(ev.target.value) || 150 })}
        />
      </label>

      <div className="flex flex-wrap gap-2 pt-1">
        <button type="button" className="btn-secondary text-xs" onClick={geocodeFromVenue} disabled={geocoding || !eventVenue}>
          <RefreshCw size={12} className={geocoding ? "animate-spin" : ""} />
          {geocoding ? "Locating…" : "Find from event address"}
        </button>
        <button type="button" className="btn-secondary text-xs" onClick={testProximity} data-testid="test-proximity">
          <Navigation size={12} /> {watching ? "Stop proximity test" : "Test proximity on this device"}
        </button>
      </div>

      {proximity ? (
        <div
          className={`text-xs font-mono px-3 py-2 border ${
            proximity.inside ? "border-[var(--primary)]/40 text-[var(--primary)]" : "border-[var(--border-default)] text-[var(--text-secondary)]"
          }`}
        >
          {proximity.inside ? "Inside geofence" : "Outside geofence"} · {Math.round(proximity.dist)}m from pin
        </div>
      ) : null}

      {!eventVenue ? (
        <p className="text-[11px] text-[var(--text-tertiary)]">Add a venue address under Event basics to auto-locate the pin.</p>
      ) : null}
    </div>
  );
}
